import React, { useState, useEffect } from 'react';
import './BookingCalendar.css';

const getMonthDays = (year, month) => {
  const date = new Date(year, month, 1);
  const days = [];
  while (date.getMonth() === month) {
    days.push(new Date(date));
    date.setDate(date.getDate() + 1);
  }
  return days;
};

const formatDate = (date) => date.toISOString().split('T')[0];

const getDateRange = (startDateStr, endDateStr) => {
  const start = new Date(`${startDateStr}T00:00:00`);
  const end = new Date(`${endDateStr}T00:00:00`);
  const range = [];

  const cursor = new Date(start);
  while (cursor <= end) {
    range.push(formatDate(cursor));
    cursor.setDate(cursor.getDate() + 1);
  }

  return range;
};

const BookingCalendar = ({ selectedDates, setSelectedDates, enableRangeSelection = false }) => {
  const today = new Date();
  const [month, setMonth] = useState(today.getMonth());
  const [year, setYear] = useState(today.getFullYear());
  const [rangeStart, setRangeStart] = useState(null);
  const [rangeEnd, setRangeEnd] = useState(null);
  const days = getMonthDays(year, month);

  useEffect(() => {
    // placeholder: keep month/year in state for future use
  }, [month, year]);

  useEffect(() => {
    if (!enableRangeSelection) {
      setRangeStart(null);
      setRangeEnd(null);
      return;
    }

    if (selectedDates.length === 0) {
      setRangeStart(null);
      setRangeEnd(null);
      return;
    }

    const sortedDates = [...selectedDates].sort();
    setRangeStart(sortedDates[0]);

    if (sortedDates.length === 1) {
      setRangeEnd(null);
      return;
    }

    setRangeEnd(sortedDates[sortedDates.length - 1]);
  }, [enableRangeSelection, selectedDates]);

  const toggleDate = date => {
    const dateStr = formatDate(date);

    if (!enableRangeSelection) {
      setSelectedDates(selectedDates.includes(dateStr)
        ? selectedDates.filter(d => d !== dateStr)
        : [...selectedDates, dateStr]);
      return;
    }

    if (!rangeStart || (rangeStart && rangeEnd)) {
      setRangeStart(dateStr);
      setRangeEnd(null);
      setSelectedDates([dateStr]);
      return;
    }

    const start = dateStr < rangeStart ? dateStr : rangeStart;
    const end = dateStr < rangeStart ? rangeStart : dateStr;
    const rangeDates = getDateRange(start, end);
    setRangeStart(start);
    setRangeEnd(end);
    setSelectedDates(rangeDates);
  };

  // removed per-request: no per-day slot generation or UI

  return (
    <div className="calendar-container">
      <div className="calendar-header">
        <button onClick={() => {
          if (month === 0) { setMonth(11); setYear(year - 1); } else { setMonth(month - 1); }
        }}>&lt;</button>
        <span>{new Date(year, month).toLocaleString('default', { month: 'long' })} {year}</span>
        <button onClick={() => {
          if (month === 11) { setMonth(0); setYear(year + 1); } else { setMonth(month + 1); }
        }}>&gt;</button>
      </div>
      {enableRangeSelection && (
        <p className="calendar-range-hint">Select a start date, then an end date.</p>
      )}
      <div className="calendar-grid">
        {days.map(day => {
          const dateStr = formatDate(day);
          const isStartBoundary = enableRangeSelection && rangeStart === dateStr;
          const isEndBoundary = enableRangeSelection && rangeEnd === dateStr;
          const isInRange = enableRangeSelection && rangeStart && rangeEnd && dateStr > rangeStart && dateStr < rangeEnd;
          const isSelected = enableRangeSelection
            ? isStartBoundary || isEndBoundary || (rangeStart === rangeEnd && isStartBoundary)
            : selectedDates.includes(dateStr);

          const className = [
            'calendar-day',
            isSelected ? 'selected' : '',
            isInRange ? 'in-range' : ''
          ].filter(Boolean).join(' ');

          return (
            <div
              key={dateStr}
              className={className}
              onClick={() => { toggleDate(day); }}
            >
              {day.getDate()}
            </div>
          );
        })}
      </div>
      {/* per-day slot UI removed */}
    </div>
  );
};

export default BookingCalendar;
